import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useDashboard } from '@/hooks/data/useDashboard';
import { useRecentServerErrors } from '@/hooks/data/useHttpStats';
import { StatsCard, type SubStat } from '@/components/StatsCard';
import { HttpErrorsCard } from '@/components/stats/HttpErrorsCard';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { GlobeIcon, ServerIcon, SettingsIcon, ShieldIcon } from '@/components/icons';
import { formatRelativeTime } from '@/utils/date';
import { formatAuditAction, getAuditActionSymbol } from '@/utils/format';
import './Dashboard.css';

/**
 * Dashboard page component with statistics cards and recent activity
 *
 * Features:
 * - Statistics cards for nodes, domains, certificates, nginx
 * - Sites with the most HTTP 5xx errors in the last hour (hidden when there are none)
 * - Recent activity feed from audit logs (admin only)
 * - Auto-refresh capability
 * - Loading states and error handling
 */
export function Dashboard() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canReadAudit = can('audit:read');

  // Activity comes from the admin-only audit log; skip the request otherwise
  const { stats, activity, isLoading, error, reload } = useDashboard({
    includeActivity: canReadAudit,
  });
  const serverErrorSites = useRecentServerErrors({ enabled: can('stats:read') });

  // Build sub-stats for each card
  const nodeSubStats: SubStat[] = stats
    ? [
        { label: 'online', value: stats.nodes.online, status: 'success' },
        { label: 'offline', value: stats.nodes.offline, status: 'danger' },
      ]
    : [];

  const domainSubStats: SubStat[] = stats
    ? [
        { label: 'active', value: stats.domains.active, status: 'success' },
        { label: 'pending', value: stats.domains.pending, status: 'warning' },
      ]
    : [];

  const certSubStats: SubStat[] = stats
    ? [
        {
          label: 'expiring soon',
          value: stats.certificates.expiring_soon,
          status: stats.certificates.expiring_soon > 0 ? 'warning' : 'muted',
        },
        {
          label: 'expired',
          value: stats.certificates.expired,
          status: stats.certificates.expired > 0 ? 'danger' : 'muted',
        },
      ]
    : [];

  const nginxSubStats: SubStat[] = stats
    ? [
        {
          label: 'failed reloads',
          value: stats.nginx.last_reload_failures,
          status: stats.nginx.last_reload_failures > 0 ? 'danger' : 'success',
        },
      ]
    : [];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard</h1>
        {error && (
          <div className="dashboard-error" role="alert">
            <span className="dashboard-error-icon">!</span>
            <span>{error}</span>
            <button
              type="button"
              className="dashboard-error-retry"
              onClick={() => reload()}
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards Grid */}
      <div className="dashboard-stats">
        <StatsCard
          title="Nodes"
          value={stats?.nodes.total ?? '-'}
          icon={<ServerIcon />}
          subStats={nodeSubStats}
          isLoading={isLoading}
          onClick={() => navigate('/nodes')}
        />
        <StatsCard
          title="Domains"
          value={stats?.domains.total ?? '-'}
          icon={<GlobeIcon />}
          subStats={domainSubStats}
          isLoading={isLoading}
          onClick={() => navigate('/domains')}
        />
        <StatsCard
          title="Certificates"
          value={stats?.certificates.total ?? '-'}
          icon={<ShieldIcon />}
          subStats={certSubStats}
          isLoading={isLoading}
          onClick={() => navigate('/certificates')}
        />
        <StatsCard
          title="NGINX Configs"
          value={stats?.nginx.configs_count ?? '-'}
          icon={<SettingsIcon />}
          subStats={nginxSubStats}
          isLoading={isLoading}
          onClick={() => navigate('/nginx')}
        />
      </div>

      <HttpErrorsCard domains={serverErrorSites} />

      {/* Recent Activity Section */}
      <Card variant="bordered" className="dashboard-activity">
        <CardHeader>
          <CardTitle as="h2">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="dashboard-activity-content">
          {isLoading ? (
            <div className="dashboard-activity-loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="activity-item-skeleton">
                  <div className="activity-skeleton activity-skeleton-time" />
                  <div className="activity-skeleton activity-skeleton-text" />
                </div>
              ))}
            </div>
          ) : activity.length > 0 ? (
            <ul className="activity-list">
              {activity.map((entry) => (
                <li key={entry.id} className="activity-item">
                  <span className="activity-icon">
                    {getAuditActionSymbol(entry.action)}
                  </span>
                  <span className="activity-time">
                    {formatRelativeTime(entry.created_at)}
                  </span>
                  <span className="activity-user">
                    {entry.user?.email || 'System'}
                  </span>
                  <span className="activity-action">
                    {formatAuditAction(entry.action, entry.resource_type)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="dashboard-activity-empty">
              {canReadAudit ? (
                <p>No recent activity to display.</p>
              ) : (
                <p className="text-muted">
                  Activity logs are only visible to administrators.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
